import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosClient from "../../api/api";
import { successToast, errorToast } from "../../adminPanel/components/toastAlerts/ToastAlerts";
import georgianCities from "../../data/georgianCities";
import { BsPlusCircle, BsX } from "react-icons/bs";
import { apiError } from "../../api/apiError";

/**
 * Delivery-address selector for the declaration forms.
 *
 * The parcel is delivered to the address picked here (stored per pack as
 * packs.user_address_id), so a customer with several addresses can send one
 * parcel home and the next one to the office. Backend: /user-addresses.
 *
 * Props:
 *   value      — selected address id (or "")
 *   onChange   — (id) => void
 *   required   — block declaration until an address exists/was picked
 */
const DeliveryAddressPicker = ({ value, onChange, required = true }) => {
  const { t } = useTranslation();

  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({ city: "", address: "", phone: "" });

  const load = async (selectId) => {
    setLoading(true);
    try {
      const { data } = await axiosClient.get("/user-addresses");
      const list = Array.isArray(data) ? data : [];
      setAddresses(list);

      // Preselect: freshly created one, else the current value, else default.
      if (selectId) {
        onChange(String(selectId));
      } else if (!value && list.length) {
        const def = list.find((a) => a.is_default) || list[0];
        onChange(String(def.id));
      }
      // Nothing to select — force the add form open so the user isn't stuck.
      if (!list.length) setAdding(true);
    } catch (err) {
      errorToast(apiError(err, t("admin_failed_to_load") || "Failed to load addresses"));
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async () => {
    if (!draft.city || !draft.address.trim()) {
      errorToast(t("fill_all_fields") || "City and address are required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axiosClient.post("/user-addresses", {
        city: draft.city,
        address: draft.address.trim(),
        phone: draft.phone ? draft.phone.trim() : null,
      });
      successToast(t("successfully") || "Added");
      setDraft({ city: "", address: "", phone: "" });
      setAdding(false);
      await load(data?.id);
    } catch (e) {
      errorToast(apiError(e, e?.response?.data?.error ? JSON.stringify(e.response.data.error) : t("something_went_wrong") || "Failed to add"));
    }
    setSaving(false);
  };

  const labelFor = (a) => {
    const city = georgianCities.find((c) => c.value === a.city);
    return `${city ? city.label : a.city} — ${a.address}${a.phone ? ` (${a.phone})` : ""}${
      a.is_highland ? " ⛰" : ""
    }`;
  };



  return (
    <div className="flex flex-col gap-2 border border-gray_300 rounded-[12px] p-3">
      <div className="flex items-center justify-between">
        <p className="text-sm sm:text-xs text-gray-600 font-semibold">
          {t("delivery_address") || "მიტანის მისამართი"}
          {required && <span className="text-red-500"> *</span>}
        </p>
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="text-teal_600 text-xs flex items-center gap-1 hover:underline"
          >
            <BsPlusCircle /> {t("add_new_address") || "ახალი მისამართი"}
          </button>
        )}
      </div>

      {loading ? (
        <div className="animate-pulse h-11 bg-gray-100 rounded-[12px]" />
      ) : (
        addresses.length > 0 && (
          <select
            className="border border-gray_300 rounded-[12px] h-12 px-3 text-sm sm:text-xs
                       focus:outline-none focus:border-teal_600"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            required={required}
          >
            <option value="" disabled>
              {t("choose_address") || "აირჩიეთ მისამართი"}
            </option>
            {addresses.map((a) => (
              <option key={a.id} value={a.id}>
                {labelFor(a)}
                {a.is_default ? " ★" : ""}
              </option>
            ))}
          </select>
        )
      )}

      {!loading && addresses.length === 0 && !adding && (
        <p className="text-xs text-red-500">
          {t("no_address_yet") || "მისამართი არ გაქვთ დამატებული — დაამატეთ გასაგრძელებლად."}
        </p>
      )}

      {adding && (
        <div className="flex flex-col gap-2 bg-gray-50 rounded-[12px] p-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              {t("add_new_address") || "ახალი მისამართი"}
            </p>
            {addresses.length > 0 && (
              <BsX
                className="text-gray-400 w-5 h-5 cursor-pointer"
                onClick={() => setAdding(false)}
              />
            )}
          </div>

          <select
            className="border border-gray_300 rounded-[12px] h-11 px-3 text-sm sm:text-xs
                       focus:outline-none focus:border-teal_600"
            value={draft.city}
            onChange={(e) => setDraft((d) => ({ ...d, city: e.target.value }))}
          >
            <option value="">{t("city") || "ქალაქი"}</option>
            {georgianCities.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>

          <input
            className="border border-gray_300 rounded-[12px] h-11 px-3 text-sm sm:text-xs
                       focus:outline-none focus:border-teal_600"
            placeholder={t("address") || "მისამართი"}
            value={draft.address}
            onChange={(e) => setDraft((d) => ({ ...d, address: e.target.value }))}
          />

          <input
            className="border border-gray_300 rounded-[12px] h-11 px-3 text-sm sm:text-xs
                       focus:outline-none focus:border-teal_600"
            placeholder={`${t("phone") || "ტელეფონი"} (${t("optional") || "არასავალდებულო"})`}
            value={draft.phone}
            onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))}
          />

          {/* type=button — this sits inside the declaration <form> and must not submit it */}
          <button
            type="button"
            disabled={saving}
            onClick={handleCreate}
            className="bg-teal_600_b2 border-2 border-solid border-teal_600 rounded-[12px]
                       h-11 text-white text-sm sm:text-xs disabled:opacity-50"
          >
            {saving ? "..." : t("save") || "შენახვა"}
          </button>
        </div>
      )}
    </div>
  );
};

export default DeliveryAddressPicker;
